import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api';

// LegacyPreview — the original filmstrip + per-page JPEG renderer that
// lived inline in DocumentDetail's ContentPane. Extracted so the v2
// shell can fall back to it for kinds that don't yet have a dedicated
// renderer (or when VITE_VIEWER_V2 is off).
//
// Phase A of the thumbnail-by-URL architecture stripped
// extraction.preview_images from the slim list response (the case-wide
// payload would have crossed Cloud Run's 32 MB cap). This component
// now fetches the metadata array from a dedicated per-doc endpoint
// and renders each frame via <img src=blob:> built from /thumbnail
// blob fetches — same caching semantics as ThumbnailGrid's tiles.
export default function LegacyPreview({ doc, documentId, totalPages, initialPage = null }) {
  const [currentPage, setCurrentPage] = useState(initialPage && initialPage > 0 ? initialPage : 1);
  const [pageInputValue, setPageInputValue] = useState(String(initialPage && initialPage > 0 ? initialPage : 1));
  const [imgSrc, setImgSrc] = useState(null);
  const [imgError, setImgError] = useState(null);
  const [selectedFrame, setSelectedFrame] = useState(null);

  // Metadata for available preview frames, fetched fresh from the
  // backend on mount. {frame, label, width, height, source_type, ...}
  const [previewMeta, setPreviewMeta] = useState([]);
  // Map<frame_index, blob URL>. Populated lazily as each filmstrip
  // <img> mounts; the per-frame fetch is cached by the browser HTTP
  // cache anyway (Cache-Control: immutable, year-long), so re-mounts
  // pay zero bytes.
  const [frameBlobs, setFrameBlobs] = useState({});

  useEffect(() => {
    if (!documentId) return;
    let cancelled = false;
    api.get(`/documents/${documentId}/preview-images`)
      .then(res => {
        if (cancelled) return;
        setPreviewMeta(Array.isArray(res?.preview_images) ? res.preview_images : []);
      })
      .catch(() => { if (!cancelled) setPreviewMeta([]); });
    return () => { cancelled = true; };
  }, [documentId]);

  // Fetch each frame's bytes via the existing /thumbnail?frame=N
  // endpoint. URL.createObjectURL builds a blob: src; same hook
  // pattern as useThumbnailUrl in the grid.
  useEffect(() => {
    if (!documentId || previewMeta.length === 0) return;
    let cancelled = false;
    const created = [];
    previewMeta.forEach(item => {
      api.raw('GET', `/documents/${documentId}/thumbnail?frame=${item.frame}`)
        .then(res => res.ok ? res.blob() : null)
        .then(blob => {
          if (cancelled || !blob) return;
          const url = URL.createObjectURL(blob);
          created.push(url);
          setFrameBlobs(prev => ({ ...prev, [item.frame]: url }));
        })
        .catch(() => { /* leave the spot empty — frame just doesn't render */ });
    });
    return () => {
      cancelled = true;
      // Revoke object URLs so the browser can GC them.
      created.forEach(u => URL.revokeObjectURL(u));
    };
  }, [documentId, previewMeta]);

  const previews = useMemo(
    () => previewMeta.map(item => ({
      frame: item.frame,
      label: item.label,
      src: frameBlobs[item.frame] || null,
    })),
    [previewMeta, frameBlobs],
  );

  useEffect(() => {
    if (!totalPages) return;
    let cancelled = false;
    setImgSrc(null);
    setImgError(null);

    api.raw('GET', `/documents/${documentId}/page/${currentPage}?width=800&quality=85`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then(blob => { if (!cancelled) setImgSrc(URL.createObjectURL(blob)); })
      .catch(err => { if (!cancelled) setImgError(err.message); });

    return () => { cancelled = true; };
  }, [documentId, currentPage, totalPages]);

  const commitPageInput = () => {
    const n = parseInt(pageInputValue, 10);
    if (!Number.isFinite(n) || !totalPages) {
      setPageInputValue(String(currentPage));
      return;
    }
    const clamped = Math.min(Math.max(1, n), totalPages);
    setCurrentPage(clamped);
    setPageInputValue(String(clamped));
  };

  const goPrev = () => {
    setCurrentPage(p => {
      const n = Math.max(1, p - 1);
      setPageInputValue(String(n));
      return n;
    });
  };
  const goNext = () => {
    setCurrentPage(p => {
      const n = Math.min(totalPages, p + 1);
      setPageInputValue(String(n));
      return n;
    });
  };

  if (previews.length > 0) {
    return (
      <div className="dd-preview">
        <h4>Preview ({previews.length} frame{previews.length !== 1 ? 's' : ''})</h4>
        <div className="dd-filmstrip">
          {previews.map((img, i) => (
            <button
              key={img.frame}
              className={`dd-filmstrip-thumb ${selectedFrame === i ? 'dd-filmstrip-active' : ''}`}
              onClick={() => setSelectedFrame(selectedFrame === i ? null : i)}
              title={img.label}
            >
              {img.src ? (
                <img src={img.src} alt={img.label} />
              ) : (
                <div className="dd-filmstrip-loading" />
              )}
              <span className="dd-filmstrip-label">{img.label}</span>
            </button>
          ))}
        </div>
        {selectedFrame !== null && previews[selectedFrame]?.src && (
          <div className="dd-filmstrip-full">
            <img src={previews[selectedFrame].src} alt={previews[selectedFrame].label} />
          </div>
        )}
      </div>
    );
  }

  if (totalPages > 0) {
    return (
      <div className="dd-preview">
        <div className="dd-preview-nav">
          <button className="btn" disabled={currentPage <= 1} onClick={goPrev}>← Prev</button>
          <span>Page</span>
          <input
            className="dv-page-input"
            type="number"
            min={1}
            max={totalPages}
            value={pageInputValue}
            onChange={(e) => setPageInputValue(e.target.value)}
            onBlur={commitPageInput}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
            aria-label="Jump to page"
          />
          <span>of {totalPages}</span>
          <button className="btn" disabled={currentPage >= totalPages} onClick={goNext}>Next →</button>
        </div>
        <div className="dd-preview-img">
          {imgError && <p className="text-muted">Failed to load page preview: {imgError}</p>}
          {!imgError && !imgSrc && <div className="dd-loading">Rendering page…</div>}
          {imgSrc && <img src={imgSrc} alt={`Page ${currentPage}`} />}
        </div>
      </div>
    );
  }

  return <p className="text-muted" style={{ padding: '8px 0' }}>No preview available for this document.</p>;
}
